<template>
    <div id="selfFindEmail">
        <FormError :hide-error="!errorCode" :error-code="errorCode"></FormError>

        <accountContainer
            class="narrow narrow-sm"
            :sub-title-text="$t('self-service.find-email.title', { lineBreak: '' })"
        >
            <template v-if="isOnlyNexonTagOption">
                <p>{{ $t('self-service.find-email.nexon-tag-desc') }}</p>
            </template>

            <template v-else>
                <p>{{ $t('self-service.find-email.desc') }}</p>

                <div class="input-container">
                    <FormSelect
                        v-model="searchBy"
                        :options="searchByOptions"
                        label-text=""
                        string-selector="self-service.find-email"
                        close-on-select
                    >
                    </FormSelect>
                </div>
            </template>

            <div class="game-select-container">
                <FormSelect
                    v-if="searchBy === 'game-character'"
                    v-model="$v.productId.$model"
                    class="game-select"
                    :options="gameList"
                    :has-error="submitted && $v.productId.$error"
                    label-text=""
                    options-value="productId"
                    options-label="productName"
                    string-selector="self-service.find-email"
                    :place-holder="$t('self-service.select-game')"
                    close-on-select
                    dont-localize
                >
                </FormSelect>

                <h5 v-if="isOnlyNexonTagOption">{{ $t('self-service.find-email.nexon-tag') }}</h5>

                <FormInput
                    v-model.trim="$v.searchValue.$model"
                    :class="{ 'character-name': searchBy === 'game-character' }"
                    type="text"
                    :has-error="submitted && $v.searchValue.$error"
                    :enter-handler="next"
                    :placeholder="searchValuePlaceholder"
                >
                </FormInput>

                <p v-if="submitted && searchBy !== 'game-character' && $v.searchValue.$error" class="help-block">
                    {{
                        $t('self-service.find-email.enter-valid-nexon-id-tag', {
                            nexonIdTagText: this.$t(`self-service.find-email.${searchBy}`),
                        })
                    }}
                </p>
                <p v-else-if="submitted && $v.productId.$error" class="help-block">
                    {{ $t('self-service.select-game-error') }}
                </p>
                <p v-else-if="submitted && searchBy === 'game-character' && $v.searchValue.$error" class="help-block">
                    {{ $t('self-service.enter-character-name-error') }}
                </p>
            </div>

            <div class="clearfix"></div>

            <FormSelect
                v-if="displayMaplestoryRegion"
                v-model="maplestoryRegion"
                :options="maplestoryRegionOptions"
                label-text=""
                string-selector="self-service.find-email"
                close-on-select
            >
            </FormSelect>

            <div class="clearfix"></div>

            <reCaptcha
                v-if="showCaptcha"
                v-model="$v.reCaptchaCode.$model"
                :has-error="$v.reCaptchaCode.$error"
                :override-error-text="$t('self-service.complete-captcha')"
                @captcha-completed="setCaptchaCode"
            >
            </reCaptcha>

            <FormButton
                class="btn-gradient"
                :loading="buttonLoading"
                :button-click="next"
                :label="this.$t('self-service.next')"
            >
            </FormButton>

            <recaptcha-attribution
                @privacy-click="trackOtherLinkClick('Privacy Policy')"
                @tos-click="trackOtherLinkClick('Terms of Service')"
            ></recaptcha-attribution>

            <ul class="help list-unstyled">
                <li>
                    <button @click="goToVerifyOwnership">
                        <svgIcons icon-holder-class="icon-help" icon-id="icon-page-right" />
                        {{ $t('self-service.cannot-find-remember') }}
                        <span v-if="isVerifyOwnershipLoading" class="spinner-circle resend"></span>
                    </button>
                </li>
                <li>
                    <router-link
                        :to="{ name: 'self-service' }"
                        @click.native="trackOtherLinkClick($t('self-service.back', 'en'))"
                    >
                        <svgIcons icon-holder-class="icon-help" icon-id="icon-page-right" />
                        {{ $t('self-service.back') }}
                    </router-link>
                </li>
            </ul>
        </accountContainer>
    </div>
</template>

<script>
import { FormError, FormButton, FormSelect, FormInput, SvgIcons } from '@arena/toolkit';
import { required, requiredIf } from 'vuelidate/lib/validators';
import accountContainer from '@/components/account-container';
import reCaptcha from '@/components/recaptcha';
import recaptchaAttribution from '@/components/recaptcha-attribution';
import { mapGetters } from 'vuex';

export default {
    name: 'SelfFindEmail',
    components: {
        accountContainer,
        FormInput,
        FormError,
        FormButton,
        FormSelect,
        reCaptcha,
        recaptchaAttribution,
        SvgIcons,
    },
    metaInfo() {
        return {
            titleTemplate: `%s | ${this.$t('self-service.find-email.title', { lineBreak: '' })}`,
        };
    },
    props: {},
    data() {
        return {
            searchBy: '',
            searchValue: '',
            maplestoryRegion: null,
            productId: null,
            errorCode: 0,
            buttonLoading: false,
            submitted: false,
            reCaptchaCode: '',
            showCaptcha: false,
            isVerifyOwnershipLoading: false,
        };
    },
    validations() {
        return {
            searchValue: {
                required,
            },
            productId: {
                required: requiredIf(() => this.searchBy === 'game-character'),
            },
            reCaptchaCode: {
                required: requiredIf(() => this.showCaptcha),
            },
        };
    },
    computed: {
        ...mapGetters({
            serialNumber: 'selfService/getSerialNumber',
            gameList: 'selfService/getGameList',
            maplestoryRegionOptions: 'selfService/getMaplestoryRegionOptions',
        }),
        isApplication() {
            return this.$store.state.isApplication;
        },
        displayGameCharacterOption() {
            return this.region === 'global';
        },
        displayNexonIdOption() {
            return this.region !== 'th' && this.region !== 'tw' && this.region !== 'japan';
        },
        searchByOptions() {
            return [
                ...(this.displayNexonIdOption ? [{ label: 'nexon-id', value: 'nexon-id' }] : []),
                { label: 'nexon-tag-eg', value: 'nexon-tag' },
                ...(this.displayGameCharacterOption ? [{ label: 'game-character', value: 'game-character' }] : []),
            ];
        },
        selectedGameName() {
            return this.productId ? this.gameList.find((item) => item.productId === this.productId)['productName'] : '';
        },
        displayMaplestoryRegion() {
            return this.productId === '10100' && this.region === 'global';
        },
        isOnlyNexonTagOption() {
            return this.searchByOptions.length === 1 && this.searchByOptions[0].value === 'nexon-tag';
        },
        searchValuePlaceholder() {
            if (this.isOnlyNexonTagOption) return this.$t('self-service.find-email.eg-johndoe');
            if (this.searchBy === 'game-character') return this.$t('self-service.enter-character-name');

            return this.$t('self-service.find-email.enter-your', {
                findEmailByText: this.$t(`self-service.find-email.${this.searchBy}`),
            });
        },
    },
    watch: {
        searchBy() {
            this.searchValue = '';
            this.productId = null;
        },
        productId() {
            if (this.displayMaplestoryRegion) {
                this.maplestoryRegion = 'NA';
            } else {
                this.maplestoryRegion = null;
            }
        },
    },
    created() {
        this.searchBy = this.searchByOptions[0].value;
        this.$store.commit('selfService/setOperationType', 'findEmail');
        if (this.displayGameCharacterOption) {
            this.$store.dispatch('selfService/fetchGameList').catch(({ errorCode }) => {
                this.errorCode = errorCode;
            });
        }
    },
    methods: {
        setCaptchaCode() {
            this.reCaptchaCode = window.grecaptcha.enterprise.getResponse(window.rcWidgetId);
        },
        goToVerifyOwnership() {
            this.$v.reCaptchaCode.$touch();
            if (!this.isVerifyOwnershipLoading && !this.$v.reCaptchaCode.$error) {
                this.isVerifyOwnershipLoading = true;
                this.$store
                    .dispatch(
                        'selfService/createAuthSession',
                        this.isApplication ? 'launcher_self_find_email' : 'web_self_find_email'
                    )
                    .then(() => {
                        this.isVerifyOwnershipLoading = false;
                        this.trackOtherLinkClick(this.$t('self-service.cannot-find-remember', 'en'));
                        this.$router.push({ name: 'self-verify-ownership' });
                    })
                    .catch(({ errorCode }) => {
                        this.errorHandling(errorCode);
                    });
            }
        },
        next() {
            this.$v.$touch();
            this.submitted = true;
            if (!this.$v.$invalid) {
                this.buttonLoading = true;
                this.errorCode = 0;
                this.$store
                    .dispatch('selfService/getAuthTypes', {
                        searchBy: this.searchBy,
                        value: this.searchValue,
                        productId: this.productId,
                        gameName: this.selectedGameName,
                        maplestoryRegion: this.maplestoryRegion,
                        captchaAction: this.isApplication ? 'launcher_self_find_email' : 'web_self_find_email',
                    })
                    .catch(({ errorCode }) => {
                        this.errorHandling(errorCode);
                    });
            }
        },
        errorHandling(errorCode) {
            this.buttonLoading = false;
            this.isVerifyOwnershipLoading = false;

            if (errorCode === 20072) {
                this.showCaptcha = true;
                dataLayer.push({
                    event: 'recoveryCaptchaTriggered',
                    'AR Recaptcha Source': this.operationType,
                });
                return;
            }
            this.errorCode = errorCode;
        },
        trackOtherLinkClick(name) {
            dataLayer.push({
                event: 'accountRecoverySelfService_OtherLinkClick',
                'AR Link Name': name,
            });
        },
    },
};
</script>

<style lang="less" scoped>
::v-deep .form-group {
    &.mobile {
        margin-top: 0px;
    }
    .form-control {
        position: relative;
        .caret {
            top: 50%;
            right: 10px;
            transform: translateY(-50%);
        }
    }
}

.game-select-container {
    ::v-deep .form-group {
        .help-block {
            display: none;
        }
    }
    .game-select {
        width: 41.66666667%;
        float: left;
        padding-right: 10px;
        ::v-deep .select {
            .btn-content {
                padding-right: 5px;
            }
        }
        ::v-deep .dropdown-menu {
            min-width: 120px;
        }
    }
    .character-name {
        width: 58.33333333%;
        float: left;
    }
    .help-block {
        color: #e63f57;
    }
}

.btn-block {
    margin-top: 5px;
}

.help {
    button {
        padding: 0;
        color: #ccc;
        text-align: left;
        .icon-help {
            width: 8px;
            height: 8px;
            opacity: 0.8;
        }
        .spinner-circle {
            width: 16px;
            height: 16px;
            margin-bottom: -3px;
            margin-left: 5px;
        }
        &:hover {
            color: #fff;
            .icon-help {
                opacity: 1;
            }
        }
    }
}
</style>
